import {useCallback, useEffect, useRef, useState} from 'react';
import type {
    BackendChatSession,
    BackendWebSocketMessage,
    ChatMessage,
    ChatSession,
    PongWebSocketMessage,
    UserWebSocketMessage
} from '../types/chatTypes';
import {useStores} from "../../../stores";

// TODO: QA: Limit file size to ~300 lines
// TODO: QA: Separate communication layer and UI management

// Do not import directly, use useChatContext (TODO: refactor to remove 'export')
export const useChat = () => {
    const {bagStore, chatApiStore} = useStores();
    const [sessions, setSessions] = useState<ChatSession[]>([]);
    const [currentSession, setCurrentSession] = useState<ChatSession | null>(null);
    const [isConnected, setIsConnected] = useState(false);
    const [hasReceivedFinalMessage, setHasReceivedFinalMessage] = useState(true);
    const [isRestoringSession, setIsRestoringSession] = useState(false);
    const wsRef = useRef<WebSocket | null>(null);
    // const processedMessageIds = useRef<Set<string>>(new Set());
    const currentSessionRef = useRef<ChatSession | null>(null);
    const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const reconnectAttemptsRef = useRef<number>(0);
    const maxReconnectAttempts = 5;
    const shouldReconnectRef = useRef<boolean>(true);

    const generateId = () => Math.random().toString(36).substring(2, 9);
    const bagId = bagStore.bagId;

    // Call health endpoint on mount
    useEffect(() => {
        chatApiStore.checkHealth();
    }, [chatApiStore]);

    const loadSessionList = useCallback(async () => {
        if (bagId === null || bagId === undefined) {
            console.log('[useChat] loadSessionList: no bagId, skipping');
            return;
        }

        console.log('[useChat] loadSessionList: fetching sessions for bagId:', bagId);
        try {
            const backendSessions: BackendChatSession[] = await chatApiStore.listSessions(bagId);
            console.log('[useChat] loadSessionList: received', backendSessions.length, 'sessions');

            // Convert backend sessions to frontend sessions
            const frontendSessions: ChatSession[] = backendSessions.map((bs) => ({
                id: bs.session_id,
                name: bs.display_name || `Chat Session`,
                messages: [], // Messages will be loaded when session is selected
                created_at: new Date(bs.last_update_time),
                updated_at: new Date(bs.last_update_time),
            }));

            setSessions(frontendSessions);
        } catch (error) {
            console.error('[useChat] Error loading session list:', error);
        }
    }, [bagId, chatApiStore]);

    // Reset chat and load session list when databag changes
    useEffect(() => {
        console.log('[useChat] bagId effect triggered, bagId:', bagId);
        // Skip if bagId is 0 (uninitialized) or null/undefined
        if (bagId && bagId !== null && bagId !== undefined) {
            // Clear current session and reset state
            setCurrentSession(null);
            setHasReceivedFinalMessage(true);

            // Close existing WebSocket connection
            shouldReconnectRef.current = false;
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
            if (wsRef.current) {
                wsRef.current.close();
            }

            // Re-enable reconnection for next session
            setTimeout(() => {
                shouldReconnectRef.current = true;
                reconnectAttemptsRef.current = 0;
            }, 100);

            // Load session list
            console.log('[useChat] Calling loadSessionList');
            loadSessionList();
        }
    }, [bagId, loadSessionList]);

    // Effect to ensure currentSession is always synced with the latest session data
    useEffect(() => {
        if (currentSession) {
            // currentSession exists, make sure it's the updated version from sessions array
            const updatedSession = sessions.find(s => s.id === currentSession.id);
            if (updatedSession && updatedSession !== currentSession) {
                setCurrentSession(updatedSession);
            }
        }
    }, [sessions, currentSession]);

    // TODO: QA: Redundant extra variable (state duplication) and introduction of race conditions
    // Keep ref in sync with state
    useEffect(() => {
        currentSessionRef.current = currentSession;
    }, [currentSession]);

    // Cleanup reconnection timeout on unmount
    useEffect(() => {
        return () => {
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
        };
    }, []);

    const createNewSession = useCallback(async () => {
        if (!bagId) {
            setCurrentSession(null)
            throw new Error(`Cannot create session: bagId is missing (bagId=${bagId})`);
        }

        const sessionId = await chatApiStore.createSession(bagId);
        const newSession: ChatSession = {
            id: sessionId,
            name: `Chat ${sessions.length + 1}`,
            messages: [],
            created_at: new Date(),
            updated_at: new Date(),
        };

        setSessions(prev => [...prev, newSession]);
        setCurrentSession(newSession);

        return newSession;
    }, [sessions.length, bagId, chatApiStore]);

    const handleAgentMessage = useCallback((data: any, messageId?: string, sessionId?: string) => {
        // Backend sends AgentEvent structure
        const content = data.content || '';
        const eventType = data.event_type || 'text';

        setSessions(prevSessions => {
            // Use session_id from backend message if available, otherwise fall back to current session
            let activeSession: ChatSession | undefined;
            if (sessionId) {
                activeSession = prevSessions.find(s => s.id === sessionId);
            } else {
                activeSession = currentSessionRef.current || prevSessions[prevSessions.length - 1];
            }

            if (!activeSession) {
                console.warn('[handleAgentMessage] No active session found for message');
                return prevSessions;
            }

            if (eventType === 'complete') {
                // Handle completion event - mark session as not waiting
                setHasReceivedFinalMessage(true);
                return prevSessions.map(session =>
                    session.id === activeSession.id
                        ? {...session, isWaitingForResponse: false}
                        : session
                );
            } else if (eventType === 'text') {
                const newMessage: ChatMessage = {
                    id: generateId(),
                    type: 'bot',
                    content: content,
                    event_type: 'text',
                    timestamp: new Date(),
                };

                const updated = prevSessions.map(session =>
                    session.id === activeSession.id
                        ? {...session, messages: [...session.messages, newMessage], updated_at: new Date()}
                        : session
                );


                return updated;
            } else if (eventType === 'function_call') {
                // Add function_call message normally
                const functionMessage: ChatMessage = {
                    id: generateId(),
                    type: 'bot',
                    content: content,
                    event_type: eventType,
                    timestamp: new Date(),
                };

                const updated = prevSessions.map(session =>
                    session.id === activeSession.id
                        ? {...session, messages: [...session.messages, functionMessage], updated_at: new Date()}
                        : session
                );

                return updated;
            } else if (eventType === 'function_response') {
                // Find and replace the last function_call message with function_response
                const updated = prevSessions.map(session => {
                    if (session.id === activeSession.id) {
                        const messages = [...session.messages];

                        // Find the last function_call message
                        for (let i = messages.length - 1; i >= 0; i--) {
                            if (messages[i].event_type === 'function_call') {
                                // Start transition animation
                                messages[i] = {
                                    ...messages[i],
                                    isTransitioning: true,
                                    previousContent: messages[i].content,
                                    previousEventType: messages[i].event_type as 'function_call',
                                };

                                // Set timeout to complete the transition
                                setTimeout(() => {
                                    setSessions(prevSessions =>
                                        prevSessions.map(s => {
                                            if (s.id === activeSession.id) {
                                                const updatedMessages = [...s.messages];
                                                if (updatedMessages[i] && updatedMessages[i].isTransitioning) {
                                                    updatedMessages[i] = {
                                                        ...updatedMessages[i],
                                                        content: content,
                                                        event_type: 'function_response',
                                                        isTransitioning: false,
                                                        previousContent: undefined,
                                                        previousEventType: undefined,
                                                        timestamp: new Date(),
                                                    };
                                                }
                                                return {...s, messages: updatedMessages, updated_at: new Date()};
                                            }
                                            return s;
                                        })
                                    );
                                }, 300); // 300ms delay for animation

                                break;
                            }
                        }

                        return {...session, messages, updated_at: new Date()};
                    }
                    return session;
                });

                return updated;
            }
            return prevSessions;
        });
    }, []);

    const handleErrorMessage = useCallback((data: any, sessionId?: string) => {
        const errorMessage: ChatMessage = {
            id: generateId(),
            type: 'bot',
            content: data.content || 'An error occurred',
            event_type: 'text',
            timestamp: new Date(),
        };

        setSessions(prev => {
            // Use session_id from backend message if available, otherwise fall back to current session
            let activeSession: ChatSession | undefined;
            if (sessionId) {
                activeSession = prev.find(s => s.id === sessionId);
            } else {
                activeSession = currentSessionRef.current || prev[prev.length - 1];
            }

            if (!activeSession) {
                return prev;
            }

            return prev.map(session =>
                session.id === activeSession.id
                    ? {...session, messages: [...session.messages, errorMessage], updated_at: new Date()}
                    : session
            );
        });

        setHasReceivedFinalMessage(true);
    }, []);

    const handleUserMessageEcho = useCallback((data: any) => {
        // The backend echoes user messages - we can use this as confirmation
        // that the message was received, but we already displayed it locally
    }, []);

    const handlePingMessage = useCallback(() => {
        // Respond to backend ping with pong
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            const sessionId = currentSessionRef.current?.id;
            const pongMessage: PongWebSocketMessage = {
                message_type: 'pong.message',
                session_id: sessionId,
                data: {
                    timestamp: Date.now(),
                },
            };
            wsRef.current.send(JSON.stringify(pongMessage));
        }
    }, []);

    const loadMessagesFromEvents = useCallback((events: any[]) => {
        const messages: ChatMessage[] = [];

        for (const event of events) {
            const messageType = event.message_type;
            const eventData = event.data;

            if (messageType === 'user.message') {
                messages.push({
                    id: event.id || eventData.id || generateId(),
                    type: 'user',
                    content: eventData.content || '',
                    timestamp: new Date(event.timestamp),
                });
            } else if (messageType === 'agent.message') {
                const eventType = eventData.event_type || 'text';
                if (eventType !== 'complete') {
                    if (eventType === 'function_response') {
                        // Find and replace the last function_call message
                        for (let i = messages.length - 1; i >= 0; i--) {
                            if (messages[i].event_type === 'function_call') {
                                // Replace the function_call with function_response
                                messages[i] = {
                                    id: event.id || eventData.id || generateId(),
                                    type: 'bot',
                                    content: eventData.content || '',
                                    event_type: 'function_response',
                                    timestamp: new Date(event.timestamp),
                                };
                                break;
                            }
                        }
                    } else {
                        // Add regular messages (text, function_call)
                        messages.push({
                            id: event.id || eventData.id || generateId(),
                            type: 'bot',
                            content: eventData.content || '',
                            event_type: eventType,
                            timestamp: new Date(event.timestamp),
                        });
                    }
                }
            }
        }

        // Check if the last event is a 'complete' event
        const lastEvent = events.length > 0 ? events[events.length - 1] : null;
        const isLastEventComplete = lastEvent &&
            lastEvent.message_type === 'agent.message' &&
            lastEvent.data?.event_type === 'complete';

        return {messages, isLastEventComplete};
    }, []);

    const refreshSessionMessages = useCallback(async (sessionId: string) => {
        try {
            const events = await chatApiStore.getSessionEvents(sessionId);
            const {messages, isLastEventComplete} = loadMessagesFromEvents(events);

            // Update the session with refreshed messages
            setSessions(prevSessions =>
                prevSessions.map(session =>
                    session.id === sessionId
                        ? {...session, messages, updated_at: new Date()}
                        : session
                )
            );

            // Set hasReceivedFinalMessage based on whether last event is complete
            setHasReceivedFinalMessage(isLastEventComplete);

            return isLastEventComplete;
        } catch (error) {
            console.error('Error refreshing session messages:', error);
            return true; // Default to allowing messages on error
        }
    }, [chatApiStore, loadMessagesFromEvents]);

    const handleBackendWebSocketMessage = useCallback((backendMessage: BackendWebSocketMessage) => {
        const sessionId = backendMessage.session_id;

        switch (backendMessage.message_type) {
            case 'agent.message':
                handleAgentMessage(backendMessage.data, backendMessage.id, sessionId);
                break;
            case 'user.message':
                // Echo of user message - display it in the chat
                handleUserMessageEcho(backendMessage.data);
                break;
            case 'error.message':
                handleErrorMessage(backendMessage.data, sessionId);
                break;
            case 'pong.message':
                break;
            case 'ping.message':
                handlePingMessage();
                break;
            default:
                break;
        }
    }, [handleAgentMessage, handleErrorMessage, handleUserMessageEcho, handlePingMessage]);

    const connectWebSocket = useCallback((sessionId: string, isReconnect: boolean = false) => {
        try {
            // Clear any existing reconnect timeout
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }

            if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.close();
            }

            const ws = chatApiStore.createWebSocket(sessionId);
            wsRef.current = ws;

            ws.onopen = async () => {
                setIsConnected(true);
                reconnectAttemptsRef.current = 0; // Reset reconnect attempts on successful connection

                // If this is a reconnect, refresh messages from backend
                if (isReconnect) {
                    console.log('WebSocket reconnected, refreshing messages for session:', sessionId);
                    await refreshSessionMessages(sessionId);
                }
            };

            ws.onmessage = (event) => {
                try {
                    const backendMessage: BackendWebSocketMessage = JSON.parse(event.data);
                    handleBackendWebSocketMessage(backendMessage);
                } catch (error) {
                    // Error parsing WebSocket message
                }
            };

            ws.onclose = () => {
                setIsConnected(false);

                // Attempt to reconnect if we should and haven't exceeded max attempts
                if (shouldReconnectRef.current && reconnectAttemptsRef.current < maxReconnectAttempts) {
                    const currentSession = currentSessionRef.current;
                    if (currentSession && currentSession.id !== 'temp') {
                        reconnectAttemptsRef.current += 1;
                        const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current - 1), 30000); // Exponential backoff, max 30s

                        console.log(`WebSocket closed. Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts})`);

                        reconnectTimeoutRef.current = setTimeout(() => {
                            connectWebSocket(currentSession.id, true);
                        }, delay);
                    }
                }
            };

            ws.onerror = (error) => {
                console.error('WebSocket error:', error);
                setIsConnected(false);
            };
        } catch (error) {
            console.error('Error connecting WebSocket:', error);
            setIsConnected(false);
        }
    }, [handleBackendWebSocketMessage, chatApiStore, maxReconnectAttempts, refreshSessionMessages]);

    const sendMessage = useCallback(async (content: string) => {
        if (!content.trim()) return // Nothing to send
        if (!hasReceivedFinalMessage) {
            console.warn('Still waiting on previous response, cannot send new message yet.');
            return;
        }
        if (!bagId) {
            console.error('Cannot send message: bagId is missing');
            return;
        }

        let session = currentSession;
        let needsWebSocketConnection = false;

        // Add user message immediately to UI
        const userMessage: ChatMessage = {
            id: generateId(),
            type: 'user',
            content,
            timestamp: new Date(),
        };

        // Disable input immediately
        setHasReceivedFinalMessage(false);

        // If session exists, mark it as waiting for response
        if (session) {
            setSessions(prev => prev.map(s =>
                s.id === session!.id
                    ? {...s, isWaitingForResponse: true}
                    : s
            ));
        }

        // If no session exists, create a temporary one immediately for UI responsiveness
        if (!session) {
            const tempSession: ChatSession = {
                id: 'temp',
                name: `Chat ${sessions.length + 1}`,
                messages: [userMessage],
                created_at: new Date(),
                updated_at: new Date(),
            };

            setSessions(prev => [...prev, tempSession]);
            setCurrentSession(tempSession);

            try {
                // Create the real session in the background (get session ID only)
                const sessionId = await chatApiStore.createSession(bagId);
                needsWebSocketConnection = true;

                if (!sessionId) {
                    // Show error message if session creation failed
                    const errorMessage: ChatMessage = {
                        id: generateId(),
                        type: 'bot',
                        content: 'Error: Failed to create a chat session. Please try again.',
                        event_type: 'text',
                        timestamp: new Date(),
                    };

                    setSessions(prev => prev.map(s =>
                        s.id === tempSession.id
                            ? {...s, messages: [...s.messages, errorMessage]}
                            : s
                    ));
                    setHasReceivedFinalMessage(true);
                    return;
                }

                // Replace temp session with real session, preserving the message
                const realSession: ChatSession = {
                    id: sessionId,
                    name: tempSession.name,
                    messages: [userMessage],
                    created_at: tempSession.created_at,
                    updated_at: new Date(),
                };

                setSessions(prev => {
                    const withoutTemp = prev.filter(s => s.id !== tempSession.id);
                    return [...withoutTemp, realSession];
                });
                setCurrentSession(realSession);
                session = realSession;
            } catch (error) {
                // Show error message
                const errorMessage: ChatMessage = {
                    id: generateId(),
                    type: 'bot',
                    content: `Error: Failed to create a chat session: ${error instanceof Error ? error.message : 'Unknown error'}. Please try again.`,
                    event_type: 'text',
                    timestamp: new Date(),
                };

                setSessions(prev => prev.map(s =>
                    s.id === tempSession.id
                        ? {...s, messages: [...s.messages, errorMessage]}
                        : s
                ));
                setHasReceivedFinalMessage(true);
                return;
            }
        } else {
            // Add user message to existing session
            setSessions(prev => {
                const updated = prev.map(s =>
                    s.id === session!.id
                        ? {...s, messages: [...s.messages, userMessage], updated_at: new Date()}
                        : s
                );

                // Update currentSession with the updated session object
                const updatedSession = updated.find(s => s.id === session!.id);
                if (updatedSession) {
                    setCurrentSession(updatedSession);
                }

                return updated;
            });
        }

        // Handle WebSocket connection and message sending
        const sendWebSocketMessage = () => {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
                const message: UserWebSocketMessage = {
                    message_type: 'user.message',
                    session_id: session?.id,
                    data: {
                        content,
                    },
                };
                wsRef.current.send(JSON.stringify(message));
            } else {
                // Fallback
                setTimeout(() => {
                }, 2000);
            }
        };

        if (needsWebSocketConnection && session.id !== 'temp') {
            // Connect WebSocket and wait for connection before sending message
            console.log('Connecting WebSocket for new session:', session.id);
            connectWebSocket(session.id);

            // Wait for WebSocket connection to be established
            const waitForConnection = () => {
                if (wsRef.current?.readyState === WebSocket.OPEN) {
                    sendWebSocketMessage();
                } else if (wsRef.current?.readyState === WebSocket.CONNECTING) {
                    // Still connecting, wait a bit more
                    setTimeout(waitForConnection, 100);
                } else {
                    // Connection failed, use fallback
                    console.log('WebSocket connection failed, using fallback');
                    sendWebSocketMessage();
                }
            };

            // Give a small delay for WebSocket to start connecting
            setTimeout(waitForConnection, 100);
        } else {
            // Use existing WebSocket connection or fallback
            sendWebSocketMessage();
        }
    }, [currentSession, sessions.length, chatApiStore, bagId, connectWebSocket, hasReceivedFinalMessage]);

    const selectSession = useCallback(async (session: ChatSession) => {
        // If session already has messages loaded, just switch to it
        if (session.messages.length > 0) {
            setCurrentSession(session);
            // Set hasReceivedFinalMessage based on session's waiting state
            setHasReceivedFinalMessage(!session.isWaitingForResponse);
            // Don't reconnect WebSocket - it's shared across all sessions
            // Only connect if there's no existing connection
            if (session.id !== 'temp' &&
                (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN)) {
                connectWebSocket(session.id);
            }
            return;
        }

        // Otherwise, load messages from backend (same as restoreSession)
        try {
            setIsRestoringSession(true);
            const events = await chatApiStore.getSessionEvents(session.id);
            const {messages, isLastEventComplete} = loadMessagesFromEvents(events);

            // Update the session with loaded messages
            const updatedSession: ChatSession = {
                ...session,
                messages,
            };

            setSessions(prev => prev.map(s => s.id === session.id ? updatedSession : s));
            setCurrentSession(updatedSession);
            setHasReceivedFinalMessage(isLastEventComplete);

            // Connect WebSocket only if not already connected
            if (session.id !== 'temp' &&
                (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN)) {
                connectWebSocket(session.id);
            }
        } catch (error) {
            console.error('Error loading session messages:', error);
            setCurrentSession(session);
            setHasReceivedFinalMessage(true);
        } finally {
            setIsRestoringSession(false);
        }
    }, [chatApiStore, loadMessagesFromEvents, connectWebSocket]);

    const startNewChat = useCallback((initialMessage?: string) => {
        setCurrentSession(null);
        setHasReceivedFinalMessage(true);

        // Stop reconnection attempts
        shouldReconnectRef.current = false;
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }

        if (wsRef.current) {
            wsRef.current.close();
        }

        // Re-enable reconnection for next session
        setTimeout(() => {
            shouldReconnectRef.current = true;
            reconnectAttemptsRef.current = 0;

            if (initialMessage) {
                sendMessage(initialMessage).then();
            }
        }, 100);
    }, []);

    const restoreSession = useCallback(async (sessionId: string) => {
        try {
            setIsRestoringSession(true);
            // Fetch session events from backend
            const events = await chatApiStore.getSessionEvents(sessionId);
            const {messages, isLastEventComplete} = loadMessagesFromEvents(events);

            // Check if session already exists in the list (loaded from backend)
            let updatedSession: ChatSession | null = null;
            setSessions(prev => {
                const existingSession = prev.find(s => s.id === sessionId);
                if (existingSession) {
                    // Session already exists, just update it with messages
                    updatedSession = {
                        ...existingSession,
                        messages,
                    };
                    return prev.map(s => s.id === sessionId ? updatedSession! : s);
                } else {
                    // Session doesn't exist, add it (shouldn't happen with backend list)
                    updatedSession = {
                        id: sessionId,
                        name: `Chat ${prev.length + 1}`,
                        messages,
                        created_at: messages.length > 0 ? messages[0].timestamp : new Date(),
                        updated_at: messages.length > 0 ? messages[messages.length - 1].timestamp : new Date(),
                    };
                    return [...prev, updatedSession];
                }
            });

            // Set current session to the updated one
            if (updatedSession) {
                setCurrentSession(updatedSession);
            }

            // Set input enabled/disabled based on whether last event is complete
            setHasReceivedFinalMessage(isLastEventComplete);

            // Connect WebSocket for this session after loading messages
            connectWebSocket(sessionId);

            return updatedSession;
        } catch (error) {
            console.error('Error restoring session:', error);
            // Don't create error sessions - just log the error
            return null;
        } finally {
            setIsRestoringSession(false);
        }
    }, [chatApiStore, loadMessagesFromEvents, sessions.length, connectWebSocket]);

    const isWaiting = Boolean(currentSession?.messages.length) && !hasReceivedFinalMessage;

    return {
        sessions,
        currentSession,
        isConnected,
        hasReceivedFinalMessage,
        isWaiting,
        isRestoringSession,
        sendMessage,
        selectSession,
        startNewChat,
        createNewSession,
        restoreSession,
    };
};
